import { useCallback, useEffect, useState } from 'react';
import { MonitorSmartphone, ShieldAlert, LogOut, CircleDot, MapPin } from 'lucide-react';
import { api, ErrorApi } from '../api';
import NotaExplicativa from '../ui/NotaExplicativa';
import ModalConfirmar from '../ui/ModalConfirmar';
import Ayuda from '../ui/Ayuda';
import Motivo from '../experiencia/Motivo';
import { fechaYHora, describirAparato } from '../ui/sesiones';
import { describirLugar } from '../ops/lugar';
import { Cargando } from '../ui/EstadoDePantalla';

// TUS SESIONES ABIERTAS · deuda T-c.
//
// QUÉ CONTESTA ESTA PANTALLA. Una pregunta que hasta ahora no tenía
// dónde hacerse:
//
//     «¿Dónde está abierta mi cuenta ahora mismo?»
//
// Y la que viene detrás, que es la que importa: «¿puedo cerrar la que no
// reconozco?». Antes la respuesta era no — cerrar sesión solo borraba el
// token del navegador, y una copia seguía entrando hasta cumplir su hora.
//
// TRES DECISIONES DE USUARIO CERO:
//
//   1. **La sesión actual va marcada, y en verde.** Sin saber cuál es la
//      suya, nadie se atreve a cerrar ninguna por miedo a echarse a sí
//      mismo. Es la razón por la que esta pantalla se usaría o no.
//   2. **El aparato se dice en español**, no con la cadena del
//      navegador. «Chrome en Windows» lo reconoce cualquiera;
//      «Mozilla/5.0 (Windows NT 10.0; Win64; x64)…» no lo reconoce nadie.
//   3. **Un solo botón, y dice exactamente qué hace.** «Cerrar las
//      demás» —no «todas»— porque la propia se queda viva a propósito:
//      echarse a sí mismo mientras intentas echar a otro es lo último
//      que quieres en ese momento.

// ---------------------------------------------------------------------
// T-1 · «MIS SESIONES EN APARATOS» (Anexo A §A2.5)
//
// VA EN ESTA MISMA PANTALLA Y NO EN UNA NUEVA, y es una decisión de
// usuario cero, no de programación: la pregunta que trae aquí a alguien
// es la misma —«¿dónde estoy abierto y cómo lo cierro?»— y la respuesta
// no debería estar en dos menús distintos según si lo que le preocupa es
// un navegador o una perilla atornillada a una repisa.
//
// La diferencia que sí se dice, porque cambia lo que hay que hacer: una
// sesión de navegador es TU CUENTA abierta en otro sitio; una sesión de
// aparato es UNA TERMINAL que está moviendo inventario a tu nombre. La
// segunda es más urgente y por eso va primero cuando la hay.
// ---------------------------------------------------------------------

function minutosDesde(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
}

function SesionesEnAparatos() {
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [cerrando, setCerrando] = useState(null);

  const cargar = useCallback(() => {
    api.get('/dispositivos/mis-sesiones')
      .then(setLista)
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar.'));
  }, []);

  useEffect(cargar, [cargar]);

  async function cerrar(s) {
    setError(null);
    setCerrando(s.id);
    try {
      const d = await api.del(`/dispositivos/sesiones/${s.id}`);
      setAviso(d.mensaje);
      cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cerrar.');
    } finally {
      setCerrando(null);
    }
  }

  // ESTADO VACÍO ÚTIL: no se esconde la sección, se explica. Quien nunca
  // ha usado una terminal descubre aquí que existen; quien acaba de
  // cerrar la suya ve que se cerró de verdad.
  if (lista !== null && lista.length === 0) {
    return (
      <section className="space-y-2">
        <h2 className="text-base font-bold text-gray-900">Terminales donde estás trabajando</h2>
        <div className="border border-gray-200 bg-white rounded-xl p-4 text-sm text-gray-600">
          Ahora mismo no estás vinculado a ninguna terminal. Cuando escanees
          el código de una, aparecerá aquí y podrás cerrarla desde tu
          teléfono sin volver a la repisa.
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h2 className="text-base font-bold text-gray-900">Terminales donde estás trabajando</h2>
      <p className="text-sm text-gray-600">
        Aparatos que están moviendo inventario a tu nombre. Si ya no estás
        delante de uno, ciérralo: la terminal se queda mirando, sin poder
        mover nada.
      </p>

      {aviso && (
        <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {aviso}
        </div>
      )}
      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}
      {lista === null && <Cargando filas={4} />}

      <div className="space-y-2">
        {(lista || []).map((s) => {
          const abierta = s.estado === 'ABIERTA';
          const quietos = minutosDesde(s.ultima_en);
          return (
            <div
              key={s.id}
              className={`border rounded-xl p-3 flex items-start gap-3 ${
                abierta ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}
            >
              <CircleDot className={`h-5 w-5 mt-0.5 shrink-0 ${abierta ? 'text-green-700' : 'text-gray-400'}`} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900">{s.aparato}</div>
                {s.ubicacion && (
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    <MapPin size={13} className="shrink-0" />
                    {s.ubicacion}{s.ubicacion_nombre ? ` · ${s.ubicacion_nombre}` : ''}
                  </div>
                )}
                <div className="text-sm text-gray-600">
                  Empezó el {fechaYHora(s.abierta_en)}
                  {quietos !== null && abierta && (
                    quietos < 1
                      ? ' · en uso ahora mismo'
                      : ` · sin tocarse desde hace ${quietos} min`
                  )}
                </div>
                {/* NUNCA UN ÍCONO VERDE QUE MIENTE: una sesión caducada
                    sigue en la lista, pero se dice que ya no vale. */}
                {!abierta && (
                  <div className="text-sm text-amber-800">
                    Ya no vale: nadie la tocó en {s.inactividad_min} minutos y se
                    cerró sola. La terminal está mirando, sin mover nada.
                  </div>
                )}
              </div>
              {abierta && (
                <button
                  type="button"
                  onClick={() => cerrar(s)}
                  disabled={cerrando === s.id}
                  className="shrink-0 inline-flex items-center gap-1.5 border border-gray-300 bg-white rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-700 disabled:opacity-50"
                >
                  <LogOut size={15} />
                  {cerrando === s.id ? 'Cerrando…' : 'Cerrar'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------
// NAVEGADORES Y TELÉFONOS · la lista, con «Cerrar» por sesión
// (TODO-SIRVE F2, 30 sep 2026)
//
// SE EXPORTA para que «Mi cuenta» pinte ESTA MISMA lista y no una copia:
// antes Mi cuenta decía «5 sesión(es)» sin enseñar cuáles, y la lista
// vivía solo aquí. Una sola lista, dos sitios donde se ve.
//
// «CERRAR» POR SESIÓN. Antes solo existía «Cerrar las demás», que echa a
// todas; quien reconoce su tableta y no el teléfono de ayer no tenía cómo
// cerrar solo el que no reconoce. La propia no lleva botón: cerrarla
// desde aquí dejaría la pantalla con una credencial muerta; para eso está
// «Salir».
// ---------------------------------------------------------------------
// EL PAÍS CON SU NOMBRE: «México», no «MX». Si el navegador no sabe
// nombrarlo, se queda el código tal cual, que tampoco miente.
function nombrePais(codigo) {
  if (!codigo) return null;
  try {
    return new Intl.DisplayNames(['es-MX'], { type: 'region' }).of(codigo) || codigo;
  } catch {
    return codigo;
  }
}

function plural(n, uno, varios) {
  return `${n} ${n === 1 ? uno : varios}`;
}

export function SesionesDelNavegador({ alCambiar }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  const [porCerrar, setPorCerrar] = useState(null);

  const cargar = useCallback(() => {
    api.get('/auth/sesiones')
      .then((d) => {
        const sesiones = (d?.sesiones || []).map((x) => ({ ...x, pais: nombrePais(x.pais) }));
        setDatos({ ...d, sesiones });
        setError(null);
      })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar la lista de sesiones. Revisa la conexión y vuelve a intentarlo.'));
  }, []);

  useEffect(cargar, [cargar]);

  async function cerrarLasDemas() {
    setError(null);
    setCerrando(true);
    try {
      const d = await api.post('/auth/sesiones/cerrar-las-demas', {});
      setAviso(d.mensaje);
      cargar();
      alCambiar?.();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cerrar.');
    } finally {
      setCerrando(false);
    }
  }

  async function cerrarUna(s) {
    const d = await api.post(`/auth/sesiones/${s.id}/cerrar`, {});
    setAviso(d.mensaje);
    cargar();
    alCambiar?.();
  }

  const lista = datos?.sesiones || [];
  const otras = lista.filter((s) => !s.es_esta).length;

  return (
    <div className="space-y-3">
      {aviso && (
        <div role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {aviso}
        </div>
      )}
      {error && <div role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg p-3">{error}</div>}

      {datos === null && !error && <Cargando filas={3} etiqueta="Cargando tus sesiones" />}

      {datos !== null && (
        <>
          {/* EL CONTADOR SALE DE LA MISMA LISTA que se pinta debajo: nunca
              dice un número distinto de las filas que se ven. */}
          <p className="text-sm text-gray-700">
            Ahora mismo tienes {plural(lista.length, 'sesión abierta', 'sesiones abiertas')} en navegadores y teléfonos.
          </p>
          <div className="space-y-2">
            {lista.map((s) => (
              <div
                key={s.id ?? s.raiz}
                className={`border rounded-xl p-3 flex items-start gap-3 ${
                  s.es_esta ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}
              >
                <MonitorSmartphone
                  className={`h-5 w-5 mt-0.5 shrink-0 ${
                    s.es_esta ? 'text-green-700' : 'text-gray-400'}`}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900">
                    {describirAparato(s.user_agent)}
                    {s.es_esta && (
                      <span className="ml-2 text-xs font-semibold text-green-800">
                        Ésta es la que estás usando
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 break-words">
                    Desde {describirLugar(s.ip, s.pais)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Empezó el {fechaYHora(s.creada_en)} · último movimiento el{' '}
                    {fechaYHora(s.ultima_en)}
                  </div>
                </div>
                {!s.es_esta && s.id != null && (
                  <button
                    type="button"
                    onClick={() => setPorCerrar(s)}
                    className="shrink-0 inline-flex items-center gap-1.5 min-h-[44px] border border-gray-300 bg-white rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    <LogOut size={15} aria-hidden="true" />
                    Cerrar
                  </button>
                )}
              </div>
            ))}
          </div>
          <Ayuda id="cuenta.sesiones.lugar" />
          <Ayuda id="cuenta.sesiones.cerrar_una" />

          <NotaExplicativa titulo="Qué hacer si hay una que no reconoces">
            Ciérrala desde aquí <strong>y cambia tu contraseña</strong>. Cerrarla
            surte efecto en su siguiente movimiento, no dentro de una hora: esa
            credencial deja de servir aunque quien la tenga no cierre nada.
          </NotaExplicativa>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={cerrarLasDemas}
              disabled={cerrando || otras === 0}
              aria-describedby={otras === 0 && !cerrando ? 'motivo-cerrar-demas' : undefined}
              className="inline-flex items-center gap-2 min-h-[44px] bg-[var(--color-marca)] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              <ShieldAlert size={16} aria-hidden="true" />
              {cerrando ? 'Cerrando…' : 'Cerrar las demás sesiones'}
            </button>
            {/* UN BOTÓN APAGADO DICE POR QUÉ. */}
            <Motivo id="motivo-cerrar-demas" cuando={otras === 0 && !cerrando} className="text-sm">
              No hay ninguna otra: ésta es la única abierta.
            </Motivo>
          </div>

          <p className="text-xs text-gray-500 flex items-start gap-1.5">
            <LogOut size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            La tuya se queda abierta.
          </p>
        </>
      )}

      {porCerrar && (
        <ModalConfirmar
          titulo="Cerrar esta sesión"
          descripcion={`${describirAparato(porCerrar.user_agent)}, desde ${describirLugar(porCerrar.ip, porCerrar.pais)}. Ese navegador deja de entrar en su siguiente movimiento; si no era tuya, cambia también tu contraseña.`}
          peligro
          etiquetaConfirmar="Cerrar sesión"
          onConfirmar={() => cerrarUna(porCerrar)}
          onCerrar={() => setPorCerrar(null)}
        />
      )}
    </div>
  );
}

export default function MisSesiones() {
  return (
    <div className="space-y-5 max-w-2xl">
      <header>
        <h1 className="text-xl font-bold text-gray-900">Tus sesiones abiertas</h1>
        <p className="text-sm text-gray-600">
          Dónde está abierta tu cuenta ahora mismo. Si ves algo que no
          reconoces, ciérralo desde aquí.
        </p>
      </header>

      <SesionesEnAparatos />

      <h2 className="text-base font-bold text-gray-900 pt-2">Navegadores y teléfonos</h2>

      <SesionesDelNavegador />
    </div>
  );
}
